import React, { useContext } from 'react';
import { AppContext } from '../context/AppContext';
import Avatar from './Avatar';
import { Menu, Search, LogOut, Smartphone } from 'lucide-react';
const Header = ({ activeTab, setActiveTab, sidebarOpen, setSidebarOpen, onSwitchToMobile }) => {
  const { currentUser, logout, users, currentLogo } = useContext(AppContext);
  const profile = users.find(u => u.UserID === currentUser?.UserID) || currentUser;

  return (
    <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between gap-4 h-20 shadow-2xs">
      {/* Hamburger menu toggle (PC & Mobile) & Logo */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2.5 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 border border-slate-200 rounded-xl cursor-pointer transition-all flex items-center justify-center shadow-2xs"
          title={sidebarOpen ? "메뉴 숨기기" : "메뉴 열기"}
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Logo in header when sidebar is closed or on mobile */}
        {!sidebarOpen && (
          <div className="flex items-center animate-in fade-in duration-200">
            <img 
              src={currentLogo} 
              alt="Clouflake" 
              className="object-contain cursor-pointer" 
              style={{ width: 'var(--logo-mobile-width)', height: 'var(--logo-mobile-height)' }} 
              onClick={() => setActiveTab('dashboard')}
            />
          </div>
        )}
      </div>

      {/* Mock Search Bar with larger fonts on PC */}
      <div className="flex-1 max-w-sm relative hidden sm:block">
        <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-slate-400">
          <Search className="w-4 h-4" />
        </span>
        <input
          type="text"
          placeholder="사내 서비스 및 메일 검색..."
          className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-500 focus:bg-white text-slate-900 placeholder-slate-400 transition-all"
        />
      </div>

      {/* Header Right widgets */}
      <div className="flex items-center gap-3 sm:gap-4 ml-auto">
        {/* Shortcuts */}
        <div className="flex items-center gap-2 border-r border-slate-200 pr-3 sm:pr-4">
          {onSwitchToMobile && (
            <button
              onClick={onSwitchToMobile}
              title="모바일 전용 화면으로 보기"
              className="p-2.5 rounded-xl border text-sm font-bold bg-slate-50 border-slate-200 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 transition-all cursor-pointer flex items-center gap-1"
            >
              <Smartphone className="w-4 h-4 text-indigo-600" />
              <span className="hidden xl:inline text-xs font-semibold">모바일뷰</span>
            </button>
          )}

          <button
            onClick={() => setActiveTab('leave')}
            title="연차 바로가기"
            className={`p-2.5 rounded-xl border text-sm font-bold transition-all cursor-pointer ${
              activeTab === 'leave'
                ? 'bg-indigo-50 border-indigo-200 text-indigo-600'
                : 'bg-slate-50 border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            ▣
          </button>
          
          <button
            onClick={() => setActiveTab('addressbook')}
            title="주소록 바로가기"
            className={`p-2.5 rounded-xl border text-sm font-bold transition-all cursor-pointer ${
              activeTab === 'addressbook'
                ? 'bg-indigo-50 border-indigo-200 text-indigo-600'
                : 'bg-slate-50 border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            ♙
          </button>

          <button
            onClick={() => setActiveTab('settings')}
            title="개인 설정 바로가기"
            className={`p-2.5 rounded-xl border text-sm font-bold transition-all cursor-pointer ${
              activeTab === 'settings'
                ? 'bg-indigo-50 border-indigo-200 text-indigo-600'
                : 'bg-slate-50 border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            ⚙
          </button>

          {currentUser.Role === 'ADMIN' && (
            <button
              onClick={() => setActiveTab('admin')}
              title="관리자 바로가기"
              className={`p-2.5 rounded-xl border text-sm font-bold transition-all cursor-pointer ${
                activeTab === 'admin'
                  ? 'bg-indigo-50 border-indigo-200 text-indigo-600'
                  : 'bg-slate-50 border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              ♧
            </button>
          )}
        </div>

        {/* User Block (Clickable to jump to settings) */}
        <div 
          onClick={() => setActiveTab('settings')}
          className="flex items-center gap-3 cursor-pointer p-1.5 rounded-xl hover:bg-slate-100 transition-colors"
          title="개인 설정 이동"
        >
          <Avatar config={profile?.AvatarConfig} size={40} />
          <div className="hidden md:block text-left">
            <div className="flex items-center gap-2">
              <strong className="text-sm font-bold text-slate-900">{profile.Name}</strong>
              {profile.Role === 'ADMIN' ? (
                <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 uppercase tracking-wider">
                  ADMIN
                </span>
              ) : (
                <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-100 text-slate-600 border border-slate-200 tracking-wider">
                  {profile.JobTitle || profile.Position || '사원'}
                </span>
              )}
            </div>
            <span className="text-xs text-slate-500 block -mt-0.5">{profile.Email}</span>
          </div>
        </div>

        {/* Logout Button */}
        <button
          onClick={logout}
          title="로그아웃"
          className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 hover:border-slate-300 text-slate-500 hover:text-rose-600 transition-all flex items-center justify-center cursor-pointer"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </div>
    </header>
  );
};

export default Header;
